import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../apiConfig";

const VISIBLE_CATEGORIES = [
  { label: "Una decisión importante", category: "Decisiones" },
  { label: "Mi familia", category: "Familia" },
  { label: "Mi matrimonio", category: "Matrimonio" },
  { label: "Mis hijos", category: "Mis hijos" },
  { label: "Mi salud", category: "Salud física" },
  { label: "La salud de alguien", category: "La salud de alguien" },
  { label: "Mi trabajo", category: "Trabajo" },
  { label: "Mis finanzas", category: "Finanzas" },
  { label: "Ansiedad o preocupación", category: "Ansiedad o preocupación" },
  { label: "Temor", category: "Temor" },
  { label: "Gratitud", category: "Gratitud" },
  { label: "Perdón", category: "Perdón" },
  { label: "Fortaleza", category: "Fortaleza" },
  { label: "Sabiduría", category: "Sabiduría" },
  { label: "Paz", category: "Paz" },
  { label: "Dirección", category: "Dirección" },
  { label: "Esperanza", category: "Esperanza" },
  {
    label: "Conflictos o relaciones difíciles",
    category: "Conflictos o relaciones difíciles",
  },
  { label: "Propósito", category: "Propósito" },
  { label: "Otro", category: "Otro" },
];

function getSessionToken() {
  try {
    const session = JSON.parse(localStorage.getItem("zedro_session"));
    return session?.token || "";
  } catch {
    return "";
  }
}

function createPrayerRequestId() {
  const cryptoApi =
    typeof window !== "undefined" ? window.crypto : null;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);

  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}

function buildPrayerSupportId(requestId) {
  const compactId = String(requestId || "")
    .replace(/-/g, "")
    .toUpperCase();

  if (!compactId) return "";

  return `CON-${compactId.slice(0, 8)}-${compactId.slice(-4)}`;
}

function addSupportId(message, error) {
  const supportId =
    String(error?.supportId || "").trim() ||
    buildPrayerSupportId(error?.requestId);

  if (!supportId) return message;

  return `${message} Código de soporte: ${supportId}.`;
}

async function readJsonResponse(response) {
  const rawText = await response.text();

  if (!rawText) {
    return {};
  }

  try {
    return JSON.parse(rawText);
  } catch {
    return {
      ok: false,
      error: "invalid_server_response",
      message:
        "Recibimos una respuesta inesperada del servidor. Tu oportunidad no fue utilizada.",
    };
  }
}

function createApiError(payload, fallbackMessage) {
  const error = new Error(
    payload?.message || payload?.error || fallbackMessage
  );

  error.code = payload?.error || "";
  error.userMessage = payload?.message || "";
  error.requestId = payload?.request_id || "";
  error.supportId = payload?.support_id || "";

  return error;
}

function buildScriptureText(passages) {
  return (passages || [])
    .filter((passage) => passage?.found && passage?.text)
    .map((passage) => `${passage.reference}\n${passage.text}`)
    .join("\n\n");
}

function getFriendlyPrayerError(error) {
  const code = String(error?.code || error?.message || "").trim();
  const serverMessage = String(error?.userMessage || "").trim();

  if (code === "daily_prayer_limit_reached") {
    return "Ya utilizaste las asistencias de oración disponibles por hoy. Puedes volver a usar esta ayuda mañana.";
  }

  if (code === "input_too_long") {
    return "El texto es demasiado extenso. Resume brevemente lo que deseas presentar e intenta nuevamente.";
  }

  if (code === "missing_content") {
    return "Escribe primero algo sobre lo que quieres orar.";
  }

  if (code === "missing_openai_key") {
    return addSupportId(
      "La ayuda para preparar la oración no está disponible en este momento. Tu oportunidad no fue utilizada. Intenta nuevamente en unos minutos.",
      error
    );
  }

  if (
    code === "ai_request_failed" ||
    code === "ai_request_timeout" ||
    code === "ai_response_parse_failed" ||
    code === "empty_prayer_response" ||
    code === "invalid_ai_response" ||
    code === "incomplete_ai_response" ||
    code === "invalid_server_response" ||
    code === "server_error"
  ) {
    return addSupportId(
      serverMessage ||
        "No pudimos preparar tu oración en este momento. Tu oportunidad no fue utilizada. Intenta nuevamente.",
      error
    );
  }

  return addSupportId(
    serverMessage ||
      code ||
      "No pudimos preparar la oración. Tu oportunidad no fue utilizada. Intenta nuevamente.",
    error
  );
}

export default function Conexion() {
  const navigate = useNavigate();

  const [view, setView] = useState("home");
  const [scriptureCategories, setScriptureCategories] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  const [passages, setPassages] = useState([]);
  const [translation, setTranslation] = useState(null);
  const [loadingPassages, setLoadingPassages] = useState(false);
  const [passagesError, setPassagesError] = useState("");

  const [needText, setNeedText] = useState("");
  const [prayerText, setPrayerText] = useState("");
  const [editingPrayer, setEditingPrayer] = useState(false);
  const [prayerError, setPrayerError] = useState("");
  const [orderingPrayer, setOrderingPrayer] = useState(false);
  const [savingPrayer, setSavingPrayer] = useState(false);
  const [message, setMessage] = useState("");
  const [usageRemaining, setUsageRemaining] = useState(null);

  const prayerTextareaRef = useRef(null);
  const prayerRequestInFlightRef = useRef(false);

  useEffect(() => {
    loadScriptureLibrary();
  }, []);

  useEffect(() => {
    if (!editingPrayer || !prayerTextareaRef.current) return;

    const textarea = prayerTextareaRef.current;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [editingPrayer, prayerText]);

  async function loadScriptureLibrary() {
    try {
      setLoadingLibrary(true);
      setMessage("");

      const res = await fetch(`${API_URL}/api/connection/scriptures`, {
        headers: {
          Authorization: `Bearer ${getSessionToken()}`,
        },
      });

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(
          json.error || "No se pudo cargar la biblioteca de Conexión."
        );
      }

      setScriptureCategories(json.categories || []);
    } catch (err) {
      console.error("[CONNECTION_SCRIPTURE_LIBRARY_ERROR]", err);
      setMessage(
        "No pudimos preparar Conexión. Actualiza la página e intenta nuevamente."
      );
    } finally {
      setLoadingLibrary(false);
    }
  }

  function scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  function resetPrayerFlow() {
    setSelectedCategory(null);
    setSelectedTheme(null);
    setPassages([]);
    setTranslation(null);
    setPassagesError("");
    setNeedText("");
    setPrayerText("");
    setEditingPrayer(false);
    setPrayerError("");
    setMessage("");
    setUsageRemaining(null);
  }

  function goHome() {
    resetPrayerFlow();
    setView("home");
    scrollToTop();
  }

  function goBackToThemes() {
    setView("themes");
    setSelectedTheme(null);
    setPassages([]);
    setTranslation(null);
    setPassagesError("");
    setNeedText("");
    setPrayerText("");
    setEditingPrayer(false);
    setPrayerError("");
    setMessage("");
    setUsageRemaining(null);
    scrollToTop();
  }

  function chooseCategory(categoryItem) {
    const categoryData = scriptureCategories.find(
      (item) => item.category === categoryItem.category
    );

    if (!categoryData) {
      setMessage(
        "Esta situación todavía no está disponible. Actualiza la página e intenta nuevamente."
      );
      return;
    }

    setSelectedCategory(categoryItem);
    setSelectedTheme(null);
    setPassages([]);
    setTranslation(null);
    setPassagesError("");
    setNeedText("");
    setPrayerText("");
    setEditingPrayer(false);
    setPrayerError("");
    setMessage("");
    setUsageRemaining(null);
    setView("themes");
    scrollToTop();
  }

  async function loadPassageTexts(references) {
    const cleanReferences = Array.from(
      new Set(
        (references || [])
          .map((reference) => String(reference || "").trim())
          .filter(Boolean)
      )
    );

    if (!cleanReferences.length) {
      setPassages([]);
      setTranslation(null);
      setPassagesError("No encontramos pasajes asociados a este enfoque.");
      return;
    }

    try {
      setLoadingPassages(true);
      setPassagesError("");
      setPassages([]);
      setTranslation(null);

      const res = await fetch(
        `${API_URL}/api/connection/scripture-texts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getSessionToken()}`,
          },
          body: JSON.stringify({
            references: cleanReferences,
          }),
        }
      );

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(
          json.message ||
            json.error ||
            "No se pudieron cargar los textos de los pasajes."
        );
      }

      setPassages(json.passages || []);
      setTranslation(json.translation || null);

      if (!json.complete) {
        setPassagesError(
          "Uno de los pasajes no pudo cargarse completo. Puedes continuar con los textos disponibles."
        );
      }
    } catch (err) {
      console.error("[CONNECTION_PASSAGE_TEXTS_ERROR]", err);
      setPassagesError(
        err.message || "No se pudieron cargar los pasajes. Intenta nuevamente."
      );
    } finally {
      setLoadingPassages(false);
    }
  }

  function chooseTheme(theme) {
    const references = (theme.passages || [])
      .map((passage) => passage.reference)
      .filter(Boolean);

    setSelectedTheme(theme);
    setNeedText("");
    setPrayerText("");
    setEditingPrayer(false);
    setPrayerError("");
    setMessage("");
    setUsageRemaining(null);
    setView("prayer");
    loadPassageTexts(references);
    scrollToTop();
  }

  async function preparePrayer() {
    if (!selectedCategory || !selectedTheme) {
      setPrayerError("Selecciona primero una situación y un enfoque de oración.");
      return;
    }

    if (prayerRequestInFlightRef.current || orderingPrayer) return;

    prayerRequestInFlightRef.current = true;

    const requestId = createPrayerRequestId();
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 45000);

    try {
      setOrderingPrayer(true);
      setPrayerError("");
      setMessage("");

      const references = passages
        .map((passage) => passage.reference)
        .filter(Boolean)
        .join(" · ");

      const context = [
        `Situación seleccionada: ${selectedCategory.label}.`,
        `Enfoque de oración: ${selectedTheme.theme}.`,
        needText.trim()
          ? `Lo que la persona desea expresar: ${needText.trim()}`
          : "La persona prefirió no escribir detalles personales. Prepara una oración general, sobria y cercana, basada únicamente en el enfoque y los pasajes compartidos.",
      ].join("\n");

      const res = await fetch(
        `${API_URL}/api/connection/prayer-assist`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getSessionToken()}`,
          },
          body: JSON.stringify({
            request_id: requestId,
            scripture_reference: references,
            scripture_text: buildScriptureText(passages),
            content: context,
            prayer_text: prayerText,
          }),
          signal: controller.signal,
        }
      );

      const json = await readJsonResponse(res);

      if (!res.ok || !json.ok) {
        throw createApiError(
          {
            ...json,
            request_id: json.request_id || requestId,
            support_id:
              json.support_id ||
              res.headers.get("X-ZEDRO-Support-Id") ||
              buildPrayerSupportId(requestId),
          },
          "No se pudo preparar la oración."
        );
      }

      const cleanPrayer = String(json.prayer || "").trim();

      if (!cleanPrayer) {
        throw createApiError(
          {
            error: "empty_prayer_response",
            message:
              "No pudimos preparar tu oración en este momento. Tu oportunidad no fue utilizada. Intenta nuevamente.",
            request_id: json.request_id || requestId,
          },
          "No se pudo preparar la oración."
        );
      }

      setPrayerText(cleanPrayer);
      setEditingPrayer(false);
      setUsageRemaining(
        json.usage?.remaining !== undefined
          ? json.usage.remaining
          : null
      );

      window.setTimeout(() => {
        document
          .getElementById("conexion-prayer-result")
          ?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
      }, 120);
    } catch (err) {
      if (!err.requestId) {
        err.requestId = requestId;
      }

      if (!err.supportId) {
        err.supportId = buildPrayerSupportId(requestId);
      }

      console.error("[CONNECTION_PREPARE_PRAYER_ERROR]", {
        requestId: err?.requestId || requestId,
        supportId: err?.supportId || buildPrayerSupportId(requestId),
        code: err?.code || err?.name || "unknown_error",
        message: err?.message || "unknown_error",
      });

      if (err?.name === "AbortError") {
        setPrayerError(
          addSupportId(
            "La preparación tardó más de lo esperado. Tu oportunidad no fue utilizada. Verifica tu conexión e intenta nuevamente.",
            { requestId }
          )
        );
      } else {
        setPrayerError(getFriendlyPrayerError(err));
      }
    } finally {
      window.clearTimeout(timeoutId);
      prayerRequestInFlightRef.current = false;
      setOrderingPrayer(false);
    }
  }

  async function savePrayerToJournal() {
    if (!prayerText.trim()) {
      setMessage("Prepara o escribe una oración antes de guardarla.");
      return;
    }

    try {
      setSavingPrayer(true);
      setMessage("");

      const scriptureReferences = passages
        .map((passage) => passage.reference)
        .filter(Boolean);

      const res = await fetch(
        `${API_URL}/api/connection/save-prayer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getSessionToken()}`,
          },
          body: JSON.stringify({
            category: selectedCategory?.category || "",
            category_label: selectedCategory?.label || "",
            theme: selectedTheme?.theme || "",
            scripture_references: scriptureReferences,
            translation_name: translation?.name || "",
            prayer_text: prayerText.trim(),
          }),
        }
      );

      const json = await res.json();

      if (!res.ok || !json.ok) {
        throw new Error(
          json.message || json.error || "No se pudo guardar la oración."
        );
      }

      setView("saved");
      setNeedText("");
      setMessage("");
      scrollToTop();
    } catch (err) {
      console.error("[CONNECTION_SAVE_PRAYER_ERROR]", err);
      setMessage(
        err.message || "No se pudo guardar la oración. Intenta nuevamente."
      );
    } finally {
      setSavingPrayer(false);
    }
  }

  const selectedCategoryData = selectedCategory
    ? scriptureCategories.find(
        (item) => item.category === selectedCategory.category
      )
    : null;

  return (
    <main className="conexionPage">
      <section className="conexionShell">
        <div className="conexionPageBack">
          <button
            type="button"
            className="z-back-pill"
            onClick={() => navigate("/dashboard")}
          >
            ← Volver al panel central
          </button>
        </div>

        <section className="conexionExperience">
          {message && (
            <div className="conexionMessage" role="status" aria-live="polite">
              {message}
            </div>
          )}

          {view === "home" && (
            <>
              <header className="conexionHomeHeader">
                <span>Conexión ZEDRO</span>
                <h1>¿Cuál es el motivo de tu oración hoy?</h1>
              </header>

              {loadingLibrary ? (
                <p className="conexionLoading">
                  Preparando tu espacio de Conexión...
                </p>
              ) : (
                <section
                  className="conexionCategoryGrid"
                  aria-label="Motivos para orientar la oración"
                >
                  {VISIBLE_CATEGORIES.map((item) => (
                    <button
                      type="button"
                      key={item.category}
                      className="conexionCategoryCard"
                      onClick={() => chooseCategory(item)}
                    >
                      <strong>{item.label}</strong>
                    </button>
                  ))}
                </section>
              )}
            </>
          )}

          {view === "themes" && selectedCategory && (
            <section className="conexionThemeView">
              <div className="conexionViewNav">
                <button
                  type="button"
                  className="conexionBackButton"
                  onClick={goHome}
                >
                  ← Volver
                </button>
              </div>

              <header className="conexionContextHeader">
                <h1>¿En qué necesitas enfocar tu oración?</h1>
              </header>

              <div className="conexionThemeGrid">
                {(selectedCategoryData?.themes || []).map((theme) => (
                  <button
                    type="button"
                    key={theme.theme}
                    className="conexionThemeCard"
                    onClick={() => chooseTheme(theme)}
                  >
                    <strong>{theme.theme}</strong>
                  </button>
                ))}
              </div>
            </section>
          )}

          {view === "prayer" &&
            selectedCategory &&
            selectedTheme && (
              <section className="conexionPrayerView">
                <div className="conexionViewNav">
                  <button
                    type="button"
                    className="conexionBackButton"
                    onClick={goBackToThemes}
                  >
                    ← Volver
                  </button>
                </div>

                <header className="conexionContextHeader conexionContextHeader--prayer">
                  <h1>{selectedTheme.theme}</h1>
                </header>

                <section className="conexionWordSection">
                  <div className="conexionWordHead">
                    <span>La Palabra nos recuerda...</span>
                    <p>Lee e interioriza estos pasajes con calma.</p>
                  </div>

                  {loadingPassages ? (
                    <p className="conexionLoading">
                      Cargando los textos de los pasajes...
                    </p>
                  ) : (
                    <>
                      {passagesError && (
                        <div className="conexionInlineWarning">
                          <p>{passagesError}</p>

                          <button
                            type="button"
                            onClick={() =>
                              loadPassageTexts(
                                (selectedTheme.passages || []).map(
                                  (passage) => passage.reference
                                )
                              )
                            }
                          >
                            Intentar nuevamente
                          </button>
                        </div>
                      )}

                      <div className="conexionPassages">
                        {passages.map((passage) => (
                          <article
                            key={passage.reference}
                            className="conexionPassageCard"
                          >
                            <strong>{passage.reference}</strong>

                            {passage.found ? (
                              <p>
                                {passage.numbered_text || passage.text}
                              </p>
                            ) : (
                              <p>
                                No pudimos cargar este texto en este momento.
                              </p>
                            )}
                          </article>
                        ))}
                      </div>

                      {translation && (
                        <p className="conexionAttribution">
                          {translation.name} ({translation.abbreviation}) ·{" "}
                          {translation.copyright_notice} ·{" "}
                          {translation.license_name} · Fuente:{" "}
                          {translation.source_name}
                        </p>
                      )}
                    </>
                  )}
                </section>

                <section className="conexionPrayerStudio">
                  {!prayerText && (
                    <div className="conexionOptionalBlock">
                    <div className="conexionBlockHeading">
                      <span>Opcional</span>
                      <h2>Preséntale a Dios lo que estás viviendo</h2>
                      <p>
                        Escribe, si lo deseas, lo que hay en tu corazón.
                        Prepararemos una oración para ayudarte a comenzar.
                      </p>
                    </div>

                    <textarea
                      value={needText}
                      onChange={(event) =>
                        setNeedText(event.target.value)
                      }
                      maxLength={1200}
                      placeholder="Puedes escribir brevemente lo que estás viviendo..."
                    />

                    <div className="conexionPrepareRow">
                      <button
                        type="button"
                        className="conexionPrimaryButton"
                        onClick={preparePrayer}
                        disabled={orderingPrayer || loadingPassages}
                        aria-busy={orderingPrayer}
                      >
                        {orderingPrayer
                          ? "Preparando tu oración..."
                          : "Preparar mi oración"}
                      </button>

                      <small className="conexionPrayerLimit">
                         Puedes preparar hasta 3 oraciones por día.
                      </small>

                      {usageRemaining !== null && (
                        <small>
                          {usageRemaining}{" "}
                          {usageRemaining === 1
                            ? "asistencia disponible hoy"
                            : "asistencias disponibles hoy"}
                        </small>
                      )}
                    </div>

                    {prayerError && (
                      <div
                        className="conexionPrayerError"
                        role="alert"
                        aria-live="assertive"
                      >
                        <p>{prayerError}</p>
                        <button
                          type="button"
                          onClick={preparePrayer}
                          disabled={orderingPrayer}
                        >
                          Intentar nuevamente
                        </button>
                      </div>
                    )}
                  </div>
                  )}

                  {prayerText && (
                    <div
                      id="conexion-prayer-result"
                      className="conexionPrayerResult"
                    >
                      <div
                        className="conexionPrayerReady"
                        role="status"
                        aria-live="polite"
                      >
                        <div>
                          <strong>Tu oración está preparada</strong>
                          <p>Léela con calma y preséntala a Dios con tus propias palabras.</p>
                        </div>
                      </div>

                      <div className="conexionBlockHeading">
                        <span>Tu oración</span>
                        <h2>Hazla tuya</h2>
                      </div>

                      {editingPrayer ? (
                        <textarea
                          ref={prayerTextareaRef}
                          value={prayerText}
                          onChange={(event) => setPrayerText(event.target.value)}
                          className="conexionPrayerTextarea"
                          rows={1}
                          aria-label="Editar oración preparada"
                        />
                      ) : (
                        <div className="conexionPrayerReading">
                          {prayerText}
                        </div>
                      )}

                      <div className="conexionPrayerActions">
                        <button
                          type="button"
                          className="conexionSaveButton"
                          onClick={savePrayerToJournal}
                          disabled={savingPrayer}
                        >
                          {savingPrayer
                            ? "Guardando..."
                            : "Guardar en Bitácora"}
                        </button>

                        <button
                          type="button"
                          className="conexionEditPrayerButton"
                          onClick={() => setEditingPrayer((current) => !current)}
                        >
                          {editingPrayer
                            ? "Finalizar edición"
                            : "Modificar oración"}
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </section>
            )}

          {view === "saved" && (
            <section className="conexionSavedState">
              <div className="conexionSavedIcon" aria-hidden="true">
                ✓
              </div>
              <span>Oración guardada</span>
              <h1>Tu oración quedó registrada en Bitácora</h1>
              <p>
                Podrás volver a ella cuando necesites recordar este momento
                de oración.
              </p>

              <div className="conexionSavedActions">
                <button
                  type="button"
                  className="conexionPrimaryButton"
                  onClick={goHome}
                >
                  Volver a Conexión
                </button>

                <button
                  type="button"
                  className="conexionSecondaryButton"
                  onClick={() => navigate("/bitacora")}
                >
                  Abrir Bitácora
                </button>
              </div>
            </section>
          )}
        </section>
      </section>

      <style>
        {`
          .conexionPage {
            min-height: 100vh;
            padding: 2rem 1.25rem 4rem;
            background: var(--page-bg);
            color: #0f2f46;
          }

          .conexionShell {
            width: min(1180px, 100%);
            margin: 0 auto;
          }

          .conexionPageBack {
            display: flex;
            align-items: center;
            margin-bottom: 1.15rem;
          }

          .conexionExperience {
            min-height: 520px;
            padding: clamp(1.2rem, 2.4vw, 2rem);
            border: 1px solid rgba(15, 47, 70, 0.07);
            border-radius: 30px;
            background:
              radial-gradient(circle at 8% 0%, rgba(204, 163, 82, 0.13), transparent 29%),
              radial-gradient(circle at 100% 14%, rgba(15, 47, 70, 0.07), transparent 27%),
              linear-gradient(135deg, rgba(250, 248, 243, 0.98), rgba(236, 241, 245, 0.98));
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.82),
              0 24px 58px rgba(15, 47, 70, 0.06);
          }

          .conexionMessage {
            margin: 0 0 1.15rem;
            padding: 0.9rem 1rem;
            border-radius: 17px;
            border: 1px solid rgba(204, 163, 82, 0.3);
            background: rgba(255, 252, 245, 0.96);
            color: #6d5524;
            font-size: 0.92rem;
            line-height: 1.55;
          }

          .conexionHomeHeader,
          .conexionContextHeader {
            max-width: 890px;
            margin-bottom: 1.4rem;
          }

          .conexionHomeHeader > span,
          .conexionBlockHeading > span,
          .conexionSavedState > span {
            display: inline-flex;
            margin-bottom: 0.65rem;
            padding: 0.36rem 0.72rem;
            border-radius: 999px;
            background: rgba(204, 163, 82, 0.13);
            color: #8a692c;
            font-size: 0.7rem;
            font-weight: 900;
            letter-spacing: 0.11em;
            text-transform: uppercase;
          }

          .conexionHomeHeader h1,
          .conexionContextHeader h1,
          .conexionSavedState h1 {
            margin: 0;
            font-size: clamp(2rem, 4vw, 3.35rem);
            font-weight: 400;
            letter-spacing: -0.05em;
            line-height: 1.05;
          }

          .conexionCategoryGrid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 0.72rem;
          }

          .conexionCategoryCard {
            position: relative;
            min-height: 78px;
            display: flex;
            align-items: center;
            padding: 0.9rem 1rem;
            overflow: hidden;
            border-radius: 18px;
            border: 1px solid rgba(15, 47, 70, 0.09);
            background:
              linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(255, 255, 255, 0.82));
            color: #0f2f46;
            text-align: left;
            font-family: Questrial, sans-serif;
            cursor: pointer;
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.9),
              0 10px 24px rgba(15, 47, 70, 0.035);
            transition:
              transform 180ms ease,
              border-color 180ms ease,
              box-shadow 180ms ease,
              background 180ms ease;
          }

          .conexionCategoryCard::before {
            content: "";
            position: absolute;
            inset: 0 auto 0 0;
            width: 3px;
            background: linear-gradient(180deg, #d9b45f, #a78132);
            opacity: 0;
            transition: opacity 180ms ease;
          }

          .conexionCategoryCard:hover {
            transform: translateY(-2px);
            border-color: rgba(204, 163, 82, 0.42);
            background: #fff;
            box-shadow: 0 17px 34px rgba(15, 47, 70, 0.075);
          }

          .conexionCategoryCard:hover::before {
            opacity: 1;
          }

          .conexionCategoryCard strong {
            font-size: 0.96rem;
            line-height: 1.35;
          }

          .conexionCategoryCard:focus-visible,
          .conexionThemeCard:focus-visible,
          .conexionPrimaryButton:focus-visible,
          .conexionSecondaryButton:focus-visible,
          .conexionSaveButton:focus-visible,
          .conexionEditPrayerButton:focus-visible,
          .conexionPrayerError button:focus-visible,
          .conexionBackButton:focus-visible {
            outline: 3px solid rgba(204, 163, 82, 0.35);
            outline-offset: 3px;
          }

          .conexionViewNav {
            display: flex;
            align-items: center;
            margin-bottom: 1rem;
          }

          .conexionBackButton {
            min-height: 42px;
            padding: 0 0.95rem;
            border-radius: 999px;
            border: 1px solid rgba(15, 47, 70, 0.11);
            background: rgba(255, 255, 255, 0.84);
            color: rgba(15, 47, 70, 0.72);
            font-family: Questrial, sans-serif;
            font-size: 0.87rem;
            font-weight: 800;
            cursor: pointer;
            box-shadow: 0 8px 18px rgba(15, 47, 70, 0.035);
            transition:
              color 180ms ease,
              border-color 180ms ease,
              background 180ms ease;
          }

          .conexionBackButton:hover {
            color: #8a692c;
            border-color: rgba(204, 163, 82, 0.38);
            background: #fff;
          }

          .conexionContextHeader h1 {
            font-size: clamp(1.85rem, 3.3vw, 2.9rem);
          }

          .conexionContextHeader--prayer {
            max-width: 1020px;
          }

          .conexionContextHeader--prayer h1 {
            font-size: clamp(1.75rem, 3vw, 2.65rem);
          }

          .conexionThemeGrid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.72rem;
          }

          .conexionThemeCard {
            min-height: 72px;
            display: flex;
            align-items: center;
            padding: 0.85rem 1rem;
            border-radius: 18px;
            border: 1px solid rgba(15, 47, 70, 0.09);
            background:
              linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(255, 255, 255, 0.84));
            color: #0f2f46;
            text-align: left;
            font-family: Questrial, sans-serif;
            cursor: pointer;
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.88),
              0 10px 24px rgba(15, 47, 70, 0.035);
            transition:
              transform 180ms ease,
              border-color 180ms ease,
              box-shadow 180ms ease;
          }

          .conexionThemeCard:hover {
            transform: translateY(-1px);
            border-color: rgba(204, 163, 82, 0.42);
            box-shadow: 0 15px 30px rgba(15, 47, 70, 0.065);
          }

          .conexionThemeCard strong {
            font-size: 0.95rem;
            line-height: 1.42;
          }

          .conexionWordSection,
          .conexionPrayerStudio {
            padding: 1.15rem;
            border-radius: 26px;
            border: 1px solid rgba(15, 47, 70, 0.08);
            background: rgba(255, 255, 255, 0.92);
          }

          .conexionWordSection {
            margin-bottom: 0.9rem;
          }

          .conexionWordHead {
            margin-bottom: 0.95rem;
          }

          .conexionWordHead span {
            display: block;
            color: #8a692c;
            font-size: 0.76rem;
            font-weight: 900;
            letter-spacing: 0.1em;
            text-transform: uppercase;
          }

          .conexionWordHead p {
            margin: 0.4rem 0 0;
            color: rgba(15, 47, 70, 0.6);
            font-size: 0.92rem;
          }

          .conexionPassages {
            display: grid;
            gap: 0.78rem;
          }

          .conexionPassageCard {
            padding: 0.95rem 1rem;
            border-radius: 18px;
            border: 1px solid rgba(40, 107, 148, 0.14);
            background:
              linear-gradient(180deg, rgba(239, 247, 255, 0.94), rgba(255, 255, 255, 0.97));
          }

          .conexionPassageCard strong {
            color: #264763;
            font-size: 0.94rem;
          }

          .conexionPassageCard p {
            margin: 0.58rem 0 0;
            color: rgba(15, 47, 70, 0.79);
            font-size: 0.97rem;
            line-height: 1.68;
            white-space: pre-wrap;
          }

          .conexionAttribution {
            margin: 0.85rem 0 0;
            color: rgba(15, 47, 70, 0.45);
            font-size: 0.72rem;
            line-height: 1.5;
          }

          .conexionInlineWarning {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.8rem;
            padding: 0.82rem;
            border-radius: 16px;
            background: rgba(204, 163, 82, 0.1);
          }

          .conexionInlineWarning p {
            margin: 0;
            color: #6f5725;
            font-size: 0.87rem;
            line-height: 1.45;
          }

          .conexionInlineWarning button {
            min-height: 40px;
            padding: 0 0.85rem;
            border-radius: 999px;
            border: 1px solid rgba(204, 163, 82, 0.35);
            background: #fff;
            color: #7b6430;
            font-family: Questrial, sans-serif;
            font-weight: 800;
            cursor: pointer;
            white-space: nowrap;
          }

          .conexionPrayerStudio {
            display: grid;
            gap: 0.9rem;
          }

          .conexionOptionalBlock,
          .conexionPrayerResult {
            padding: 1rem;
            border-radius: 21px;
            border: 1px solid rgba(15, 47, 70, 0.09);
            background: rgba(249, 250, 250, 0.88);
          }

          .conexionPrayerResult {
            scroll-margin-top: 120px;
            border-color: rgba(204, 163, 82, 0.3);
            background:
              radial-gradient(circle at top right, rgba(204, 163, 82, 0.11), transparent 40%),
              rgba(255, 252, 245, 0.94);
          }

          .conexionBlockHeading h2 {
            margin: 0;
            font-size: 1.28rem;
            font-weight: 600;
            letter-spacing: -0.025em;
          }

          .conexionBlockHeading p {
            max-width: 760px;
            margin: 0.45rem 0 0;
            color: rgba(15, 47, 70, 0.59);
            font-size: 0.88rem;
            line-height: 1.58;
          }

          .conexionOptionalBlock textarea,
          .conexionPrayerTextarea {
            width: 100%;
            min-height: 118px;
            box-sizing: border-box;
            margin-top: 0.85rem;
            padding: 0.9rem;
            border-radius: 17px;
            border: 1px solid rgba(15, 47, 70, 0.13);
            background: #fff;
            color: #0f2f46;
            font-family: Questrial, sans-serif;
            font-size: 1rem;
            line-height: 1.62;
            resize: vertical;
            outline: none;
          }

          .conexionPrayerTextarea {
            min-height: 170px;
            overflow: hidden;
            resize: none;
          }

          .conexionPrayerReading {
            margin-top: 1rem;
            padding: 0.15rem 0.1rem;
            color: #0f2f46;
            font-size: 1.08rem;
            line-height: 1.78;
            white-space: pre-wrap;
            overflow-wrap: anywhere;
          }

          .conexionPrayerReady {
            display: flex;
            align-items: center;
            gap: 0.8rem;
            margin-bottom: 1.15rem;
            padding: 0.9rem 1rem;
            border: 1px solid rgba(40, 107, 148, 0.2);
            border-radius: 18px;
            background: rgba(239, 247, 255, 0.92);
            box-shadow: 0 10px 24px rgba(15, 47, 70, 0.05);
          }

          .conexionPrayerReady strong {
            display: block;
            color: #264763;
            font-size: 1rem;
          }

          .conexionPrayerReady p {
            margin: 0.18rem 0 0;
            color: rgba(15, 47, 70, 0.62);
            font-size: 0.82rem;
            line-height: 1.45;
          }

          .conexionPrayerError {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.8rem;
            margin-top: 0.85rem;
            padding: 0.85rem;
            border: 1px solid rgba(163, 72, 58, 0.2);
            border-radius: 16px;
            background: rgba(255, 247, 245, 0.96);
          }

          .conexionPrayerError p {
            margin: 0;
            color: #7f3f35;
            font-size: 0.88rem;
            line-height: 1.5;
          }

          .conexionPrayerError button,
          .conexionEditPrayerButton {
            min-height: 42px;
            padding: 0 0.95rem;
            border-radius: 999px;
            border: 1px solid rgba(15, 47, 70, 0.14);
            background: #fff;
            color: #264763;
            font-family: Questrial, sans-serif;
            font-size: 0.85rem;
            font-weight: 900;
            cursor: pointer;
          }

          .conexionPrayerError button:disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .conexionPrayerActions {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.7rem;
            margin-top: 1rem;
          }

          .conexionOptionalBlock textarea:focus,
          .conexionPrayerTextarea:focus {
            border-color: rgba(204, 163, 82, 0.55);
            box-shadow: 0 0 0 4px rgba(204, 163, 82, 0.09);
          }

          .conexionPrepareRow {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.75rem;
            margin-top: 0.8rem;
          }

          .conexionPrepareRow small {
            color: rgba(15, 47, 70, 0.48);
            font-size: 0.76rem;
          }

          .conexionPrimaryButton,
          .conexionSecondaryButton,
          .conexionSaveButton {
            min-height: 46px;
            padding: 0 1.15rem;
            border-radius: 999px;
            font-family: Questrial, sans-serif;
            font-size: 0.9rem;
            font-weight: 900;
            cursor: pointer;
          }

          .conexionPrimaryButton,
          .conexionSaveButton {
            border: 1px solid rgba(204, 163, 82, 0.5);
            background: #0f2f46;
            color: #f3d27b;
            box-shadow: 0 12px 26px rgba(15, 47, 70, 0.14);
          }

          .conexionSecondaryButton {
            border: 1px solid rgba(15, 47, 70, 0.13);
            background: #fff;
            color: #0f2f46;
          }

          .conexionSaveButton {
            margin-top: 0;
          }

          .conexionPrimaryButton:disabled,
          .conexionSecondaryButton:disabled,
          .conexionSaveButton:disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .conexionSavedState {
            max-width: 690px;
            margin: 1.4rem auto 0;
            padding: 2.2rem 1.4rem;
            border-radius: 30px;
            border: 1px solid rgba(204, 163, 82, 0.28);
            background:
              radial-gradient(circle at top, rgba(204, 163, 82, 0.13), transparent 42%),
              rgba(255, 255, 255, 0.94);
            text-align: center;
            box-shadow: 0 24px 58px rgba(15, 47, 70, 0.08);
          }

          .conexionSavedIcon {
            width: 54px;
            height: 54px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0.9rem;
            border-radius: 50%;
            background: #0f2f46;
            color: #f2d27a;
            font-size: 1.35rem;
            font-weight: 900;
          }

          .conexionSavedState h1 {
            font-size: clamp(1.8rem, 3vw, 2.55rem);
          }

          .conexionSavedState p {
            max-width: 520px;
            margin: 0.8rem auto 0;
            color: rgba(15, 47, 70, 0.62);
            line-height: 1.65;
          }

          .conexionSavedActions {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 0.7rem;
            margin-top: 1.2rem;
          }

          .conexionLoading {
            margin: 0;
            padding: 1rem;
            border-radius: 17px;
            background: rgba(255, 255, 255, 0.9);
            color: rgba(15, 47, 70, 0.58);
            line-height: 1.55;
          }

          @media (max-width: 980px) {
            .conexionCategoryGrid {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }
          }

          @media (max-width: 760px) {
            .conexionPage {
              padding: 1.2rem 0.85rem 3rem;
            }

            .conexionExperience {
              padding: 1rem;
              border-radius: 24px;
            }

            .conexionCategoryGrid,
            .conexionThemeGrid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .conexionCategoryCard {
              min-height: 72px;
            }

            .conexionWordSection,
            .conexionPrayerStudio {
              padding: 0.9rem;
              border-radius: 22px;
            }

            .conexionInlineWarning {
              align-items: stretch;
              flex-direction: column;
            }

            .conexionInlineWarning button {
              width: 100%;
            }
          }

          @media (max-width: 520px) {
            .conexionCategoryGrid,
            .conexionThemeGrid {
              grid-template-columns: 1fr;
            }

            .conexionCategoryCard,
            .conexionThemeCard {
              min-height: 64px;
            }

            .conexionHomeHeader h1 {
              font-size: 2.05rem;
            }

            .conexionContextHeader h1 {
              font-size: 1.9rem;
            }

            .conexionPrimaryButton,
            .conexionSecondaryButton,
            .conexionSaveButton {
              width: 100%;
            }

            .conexionPrepareRow {
              align-items: stretch;
              flex-direction: column;
            }

            .conexionSavedActions {
              align-items: stretch;
              flex-direction: column;
            }
          }

          @media (max-width: 520px) {
            /* nuevo ajuste de amplitud para Conexión */
            .conexionPage {
              padding: 0.9rem 0.5rem 2.5rem;
            }

            .conexionPageBack {
              margin: 0 0.25rem 0.85rem;
            }

            .conexionExperience {
              min-height: auto;
              padding: 0;
              border: 0;
              border-radius: 0;
              background: transparent;
              box-shadow: none;
            }

            .conexionHomeHeader,
            .conexionContextHeader {
              padding: 0 0.25rem;
              margin-bottom: 1.15rem;
            }

            .conexionViewNav {
              padding: 0 0.25rem;
            }

            .conexionWordSection {
              padding: 0.9rem 0.75rem;
              border-radius: 20px;
            }

            .conexionPrayerStudio {
              padding: 0;
              border: 0;
              border-radius: 0;
              background: transparent;
            }

            .conexionOptionalBlock,
            .conexionPrayerResult {
              padding: 1rem 0.85rem;
              border-radius: 20px;
            }

            .conexionPassageCard {
              padding: 0.9rem 0.85rem;
              border-radius: 16px;
            }

            .conexionOptionalBlock textarea,
            .conexionPrayerTextarea {
              margin-top: 0.75rem;
              padding: 0.85rem;
              border-radius: 15px;
              font-size: 1rem;
              line-height: 1.55;
            }

            .conexionOptionalBlock textarea {
              min-height: 140px;
            }

            .conexionPrayerTextarea {
              min-height: 180px;
            }

            .conexionPrayerReading {
              font-size: 1.04rem;
              line-height: 1.74;
            }

            .conexionPrayerReady {
              align-items: flex-start;
              padding: 0.85rem;
            }

            .conexionPrayerActions,
            .conexionPrayerError {
              align-items: stretch;
              flex-direction: column;
            }

            .conexionEditPrayerButton,
            .conexionPrayerError button {
              width: 100%;
            }

            .conexionBlockHeading p {
              max-width: none;
            }

            .conexionSaveButton {
              min-height: 52px;
            }

            .conexionPrayerLimit {
              display: block;
              margin-top: 7px;
              font-size: 11px !important;
              line-height: 1.3;
              font-weight: 400;
              color: #7a8491;
              text-align: left;
              letter-spacing: 0;
            }
          }
        `}
      </style>
    </main>
  );
}
